import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

export interface EffectiveRate {
  visits_per_year: number;
  interval_days: number | null;
  grace_days: number | null;
  source: string;
  note: string | null;
}

export interface CourtMinimum {
  id: string;
  visits_per_year: number | null;
  effective_from: string;
  basis: string;
  note: string | null;
  court_order_id: string | null;
  signed_date: string | null;
  court_index_number: string | null;
  recorded_by: string | null;
}

export interface Adjustment {
  id: string;
  visits_per_year: number;
  reason_source: string;
  note: string;
  effective_from: string;
  ended_on: string | null;
  is_current: boolean;
  set_by: string | null;
}

export interface Exemption {
  id: string;
  reason_code: string;
  reason: string;
  note: string;
  review_on: string | null;
  granted_on: string;
  ended_on: string | null;
  is_current: boolean;
  granted_by: string | null;
}

export interface VisitStandard {
  effective: EffectiveRate | null;
  court_minimum: CourtMinimum[];
  adjustment: Adjustment[];
  exemption: Exemption[];
  /** The one sentence the client page and the court report both use. */
  exempt_against_an_order: { disclosure: string; court_minimum: number } | null;
  /**
   * 068: what the appointment's kind makes of a current exemption, whether its
   * reason has been checked, and the sentences the court report uses too.
   */
  exemption_disclosure: {
    kind_state: 'kind unreviewed' | 'owes visits' | 'owes nothing' | 'no appointment on record';
    review_state: 'never' | 'overdue' | 'due' | 'current';
    below_statutory_floor: boolean;
    statutory_disclosure: string | null;
    staleness_disclosure: string | null;
    redundant_disclosure: string | null;
    reviewed_on: string | null;
    reviewed_by: string | null;
  } | null;
  exemption_reasons: { code: string; label: string; detail: string | null }[];
  /** Reading an order — `legal`. */
  may_record_minimum: boolean;
  /** Deciding for the agency — `visit_exemption`. */
  may_adjust: boolean;
}

export function useVisitStandard(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'visit-standard'],
    queryFn: () => apiFetch<VisitStandard>(`/clients/${clientId}/visit-standard`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/**
 * Every write here changes how often the client must be seen, so every one
 * invalidates the same things: the client (its header shows the rate), the
 * visits board and the dashboards that count arrears.
 */
function useStandardMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<VisitStandard>(`/clients/${clientId}/${path}`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
    },
  });
}

export const useRecordCourtMinimum = (clientId: string) =>
  useStandardMutation<{
    visits_per_year: number | null;
    court_order_id?: string;
    effective_from: string;
    basis: string;
    note?: string;
  }>(clientId, 'court-minimum');

export const useRecordAdjustment = (clientId: string) =>
  useStandardMutation<{
    visits_per_year: number;
    reason_source: string;
    note: string;
  }>(clientId, 'adjustment');

export const useEndAdjustment = (clientId: string) =>
  useStandardMutation<{ reason?: string }>(clientId, 'adjustment/end');

export const useRecordExemption = (clientId: string) =>
  useStandardMutation<{ reason_code: string; note: string; review_on?: string }>(
    clientId,
    'exemption',
  );

/** Ending says why: it is how everyone else learns the reason has gone (068). */
export const useEndExemption = (clientId: string) =>
  useStandardMutation<{ reason: string }>(clientId, 'exemption/end');

/** Checked, still true: keep it, and when to look again (068). */
export const useReviewExemption = (clientId: string) =>
  useStandardMutation<{ review_on?: string }>(clientId, 'exemption/review');
